import React, { useState } from "react";
import {
  ChevronDown, ChevronUp, RefreshCw, CalendarRange, Download, Users, UserPlus,
  Coins, Target, Filter, ArrowDownLeft, TrendingUp,
} from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, ListovaniMesice, Dlazdice, ErrorNote, Hlaska, SkeletonCards } from "../components/ui.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, RadkyPrehledu, RadekPrehledu, SkupinaPrehledu,
} from "../components/PrehledDlazdice.jsx";
import ClientLink from "../components/ClientLink.jsx";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { useApiData } from "../lib/api.js";
import MesicniPriority from "../components/MesicniPriority.jsx";
import { fmt, formatDateTime } from "../lib/format.js";

// 13.9.2026 (fáze 4f, stejný střih jako Týdenní přehled): hlavička s názvem,
// stavem měsíce v rohu ikony a akcemi, pod ní listování měsíci, čtyři dlaždice
// s hlavními čísly a pak karty s tmavou hlavičkou. Čísla, atribuce a nápovědy
// zůstávají přesně jak byly, mění se jen skladba stránky.

function parseMonthKey(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  return { year: y, month: m };
}
function formatMonthKey(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}
function addMonths(monthKey, delta) {
  const { year, month } = parseMonthKey(monthKey);
  const d = new Date(year, month - 1 + delta, 1);
  return formatMonthKey(d.getFullYear(), d.getMonth() + 1);
}
function monthLabel(monthKey) {
  const { year, month } = parseMonthKey(monthKey);
  const names = ["Leden","Únor","Březen","Duben","Květen","Červen","Červenec","Srpen","Září","Říjen","Listopad","Prosinec"];
  return `${names[month - 1]} ${year}`;
}

const MIN_MONTH_KEY = "2026-03";

// Stav měsíce: barva tečky v rohu ikony stránky a text, který se po klepnutí
// na ni ukáže.
const STAVY = {
  current: {
    tecka: "ok",
    napoveda: "Tenhle měsíc právě probíhá, čísla se ještě mění. Ustálí se, až měsíc skončí.",
  },
  past: {
    tecka: "neutralni",
    napoveda: "Měsíc je uzavřený, čísla se už nemění.",
  },
  future: {
    tecka: "prazdna",
    napoveda: "Budoucí měsíc, data k němu zatím nejsou.",
  },
};

// Rozbalovací blok se seznamem jmen. Chevron v rámečku vpravo a podbarvení
// v otevřeném stavu je stejné jako u úkolů manažera, ať je poznat, že se to
// dá rozkliknout.
function RozbalovaciJmena({ nazev, pocet, tonPoctu = "bg-slate-100 text-slate-600", jmena, poznamka }) {
  const [otevreno, setOtevreno] = useState(false);
  const maJmena = Array.isArray(jmena) && jmena.length > 0;
  if (!maJmena) {
    return (
      <div className="flex min-h-[48px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
        <span className="min-w-0 flex-1 text-sm font-bold text-slate-800">{nazev}</span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${tonPoctu}`}>{pocet}</span>
      </div>
    );
  }
  return (
    <div className={`overflow-hidden rounded-xl border ${otevreno ? "border-brand-200" : "border-slate-200"}`}>
      <button
        type="button"
        onClick={() => setOtevreno((o) => !o)}
        aria-expanded={otevreno}
        className={`flex min-h-[48px] w-full items-center gap-2 px-3 text-left ${otevreno ? "bg-brand-light" : "bg-white"}`}
      >
        <span className={`min-w-0 flex-1 text-sm font-bold ${otevreno ? "text-brand-950" : "text-slate-800"}`}>{nazev}</span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${tonPoctu}`}>{pocet}</span>
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border bg-white ${otevreno ? "border-brand-200 text-brand-deep" : "border-slate-200 text-slate-500"}`}>
          {otevreno ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {otevreno && (
        <div className="divide-y divide-slate-100 bg-white">
          {jmena.map((n, i) => (
            <div key={i} className="px-3 py-2 text-sm demo-sensitive">
              <ClientLink name={n} className="text-slate-700" />
            </div>
          ))}
          {poznamka && (
            <div className="bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500">{poznamka}</div>
          )}
        </div>
      )}
    </div>
  );
}

// Plnění jednoho cíle. Prahy barev: do 50 % sytější červená, 50 až 70 %
// světlejší červená, 70 až 90 % oranžová, od 90 % zelená.
function DlazdiceCile({ title, icon: Ikona, actual, goal, suffix = "Kč" }) {
  const pct = goal > 0 ? Math.min(Math.round((actual / goal) * 100), 200) : 0;
  const s = pct >= 90
    ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", bar: "bg-emerald-500" }
    : pct >= 70
      ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", bar: "bg-amber-500" }
      : pct >= 50
        ? { bg: "bg-rose-50/70", border: "border-rose-200", text: "text-rose-500", bar: "bg-rose-300" }
        : { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-700", bar: "bg-rose-500" };
  return (
    <div className={`min-w-0 rounded-xl border p-3 ${s.bg} ${s.border}`}>
      <div className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-600">
        {Ikona && <Ikona className="h-4 w-4 shrink-0" />}
        <span className="truncate">{title}</span>
      </div>
      <div className={`text-3xl font-bold tabular-nums sm:text-4xl ${s.text}`}>{pct} %</div>
      <div className="mt-1 truncate text-sm tabular-nums text-slate-500">
        {fmt(actual)} z {fmt(goal)} {suffix}
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
        <div className={`h-full ${s.bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

export default function Monthly() {
  const now = new Date();
  const currentKey = formatMonthKey(now.getFullYear(), now.getMonth() + 1);
  const [monthKey, setMonthKey] = useState(currentKey);
  const { blurred } = useDemoMode();
  const isFuture = monthKey > currentKey;
  const isBeforeMin = monthKey < MIN_MONTH_KEY;

  const { data, loading, error, refreshing, refetch } = useApiData(
    (isFuture || isBeforeMin) ? null : `/api/data-monthly?month=${monthKey}`,
  );

  const handlePrint = () => window.print();
  const status = monthKey === currentKey ? "current" : monthKey < currentKey ? "past" : "future";
  const stav = STAVY[status];

  // Funnel: všechno z leadů vzniklých v tomto měsíci podle aktuálního stage.
  // Trialů = leady, co prošly Trial a dál. Čekají = Trialů minus Deal minus
  // Prohra, takže matematika sedí.
  const leads = data?.leads || 0;
  const nqLeads = data?.nqLeads || 0;
  const realized = data?.trialLeadsThisMonth ?? 0;
  const deal = data?.dealThisMonth ?? 0;
  const loss = data?.lossThisMonth ?? 0;
  const undecided = Math.max(0, realized - deal - loss);
  const leadToTrialPct = leads > 0 ? Math.round((realized / leads) * 100) : 0;
  const leadToNqPct = leads > 0 ? Math.round((nqLeads / leads) * 100) : 0;
  const trialToDealPct = realized > 0 ? Math.round((deal / realized) * 100) : 0;
  const successPct = (deal + loss) > 0 ? Math.round((deal / (deal + loss)) * 100) : 0;

  // Marketing
  const avgDeal = data && data.trialDealCount > 0 && data.trialDealRevenue > 0
    ? Math.round(data.trialDealRevenue / data.trialDealCount)
    : 0;
  const costPerTrial = data && data.trialCount > 0 ? Math.round(data.mktSpend / data.trialCount) : 0;
  const costPerNewClient = data && data.trialDealCount > 0 ? Math.round(data.mktSpend / data.trialDealCount) : 0;
  const tonRoi = (pct) => (pct >= 300 ? "text-emerald-700" : pct >= 150 ? "text-amber-700" : "text-rose-700");

  return (
    <Stranka className={`print:single-page print:px-0 print:py-0 ${blurred ? "demo-blur" : ""}`}>
      <div className="print:hidden">
        <GradientHeader
          title="Měsíční přehled"
          icon={CalendarRange}
          loading={loading || refreshing}
          napoveda={stav.napoveda}
          tecka={stav.tecka}
          akce={[
            {
              klic: "obnovit",
              ikona: RefreshCw,
              popis: "Obnovit",
              onClick: refetch,
              disabled: loading || refreshing || isFuture || isBeforeMin,
            },
            {
              klic: "pdf",
              ikona: Download,
              popis: "Exportovat PDF",
              onClick: handlePrint,
              disabled: loading || !data,
            },
          ]}
        />
      </div>

      {/* Hlavička pro tisk */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-bold text-slate-900">Měsíční report, {monthLabel(monthKey)}</h1>
        <p className="mt-1 text-sm text-slate-500">Vygenerováno: {formatDateTime(new Date())}</p>
      </div>

      <div className="print:hidden">
        <ListovaniMesice
          popis={monthLabel(monthKey)}
          onZpet={() => setMonthKey(addMonths(monthKey, -1))}
          onVpred={() => setMonthKey(addMonths(monthKey, 1))}
          zpetVypnuto={monthKey <= MIN_MONTH_KEY}
          vpredVypnuto={monthKey >= currentKey}
          naDnesek={monthKey !== currentKey ? () => setMonthKey(currentKey) : undefined}
          popisDneska="Na tento měsíc"
        />
      </div>

      {/* Priority měsíce, owner je edituje a označuje splněno, ostatní čtou */}
      <MesicniPriority mesic={monthKey} reportData={data} />

      {isFuture && (
        <Hlaska ton="pozor" title="Budoucí měsíc">Data k němu zatím nejsou.</Hlaska>
      )}
      {isBeforeMin && (
        <Hlaska ton="pozor" title="Starší měsíc">
          Data jsou sledovaná od března 2026, starší měsíce nejsou k dispozici.
        </Hlaska>
      )}

      {loading && !isFuture && !isBeforeMin && <SkeletonCards count={4} />}

      {error && !isFuture && !loading && (
        <ErrorNote onRetry={refetch}>Nepodařilo se načíst data za {monthLabel(monthKey)}: {error}</ErrorNote>
      )}

      {data && !loading && !isFuture && !isBeforeMin && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Dlazdice
              popisek="Aktivní klienti"
              hodnota={fmt(data.activeClients)}
              ton="znacka"
              doplnky={
                <span className="text-xs text-slate-500">
                  {data.addedClients || 0} přibylo, {data.endedClients || 0} skončilo
                </span>
              }
            />
            <Dlazdice
              popisek="Revenue"
              hodnota={`${fmt(data.revenue)} Kč`}
              ton="ok"
              doplnky={<span className="text-xs text-slate-500">{data.paidCount} zaplacených faktur</span>}
            />
            <Dlazdice
              popisek="MKT spend"
              hodnota={`${fmt(data.mktSpend)} Kč`}
              ton="pozor"
              doplnky={<span className="text-xs text-slate-500">za celý {monthLabel(monthKey).toLowerCase()}</span>}
            />
            <Dlazdice
              popisek="ROI"
              hodnota={data.roiPct > 0 ? `${data.roiPct} %` : "zatím nic"}
              ton="info"
              hodnotaTon={data.roiPct >= 300 ? "ok" : data.roiPct >= 150 ? "pozor" : data.roiPct > 0 ? "problem" : "neutralni"}
              doplnky={<span className="text-xs text-slate-500">{fmt(data.cpl)} Kč za lead</span>}
            />
          </div>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Users}
              nazev="Klienti"
              podtitulek="kdo trénoval"
              odznak={`${fmt(data.activeClients)} aktivních`}
              napoveda="Aktivní klient je ten, kdo měl v měsíci aspoň jeden potvrzený osobní trénink. Zdroj jsou rezervace."
            />
            <div className="space-y-2 p-3 sm:p-4">
              <RozbalovaciJmena
                nazev="Přibylo klientů"
                pocet={data.addedClients || 0}
                tonPoctu="bg-emerald-50 text-emerald-700"
                jmena={data.addedClientsNames}
                poznamka="Klienti se stavem Aktivní, jejichž začátek spolupráce spadá do tohoto měsíce."
              />
              <RozbalovaciJmena
                nazev="Skončilo klientů"
                pocet={data.endedClients || 0}
                tonPoctu="bg-rose-50 text-rose-700"
                jmena={data.endedClientsNames}
                poznamka="Klienti se stavem Ukončeno, jejichž konec spolupráce spadá do tohoto měsíce."
              />
            </div>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Coins}
              nazev="Peníze"
              podtitulek="co přišlo a co ušlo"
              odznak={`${fmt(data.revenue)} Kč`}
              napoveda="Součet zaplacených faktur vystavených v tomto měsíci podle FAPI. Zahrnuje nové klienty i zkušební tréninky, DPH se bere z pole total_vat."
            />
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Zaplaceno"
                napoveda="Faktury vystavené v tomto měsíci, které už klient zaplatil."
                hodnota={`${fmt(data.revenue)} Kč`}
                doplnek={`${data.paidCount} faktur`}
                tonHodnoty="text-emerald-700"
              />
              {data.onTheWayCount > 0 && (
                <RadekPrehledu
                  popisek="Na cestě"
                  napoveda="Vystavené faktury, které ještě nebyly zaplacené a nejsou po splatnosti. Peníze se očekávají."
                  hodnota={`${fmt(data.onTheWayRevenue)} Kč`}
                  doplnek={`${data.onTheWayCount} faktur`}
                  tonHodnoty="text-amber-700"
                />
              )}
              <RadekPrehledu
                popisek="Z toho DPH"
                hodnota={`${fmt(data.revenueVat)} Kč`}
              />
            </RadkyPrehledu>

            {(data.newClientRevenue > 0 || data.trialRevenue > 0) && (
              <>
                <SkupinaPrehledu nazev="Odkud obrat je" />
                <RadkyPrehledu>
                  {data.newClientRevenue > 0 && (
                    <RadekPrehledu
                      popisek="Noví klienti"
                      napoveda={`Obrat z faktur klientů se stavem Aktivní a začátkem spolupráce v tomto měsíci. Zkušební tréninky se sem nepočítají. Z toho DPH ${fmt(data.newClientRevenueVat)} Kč.`}
                      hodnota={`${fmt(data.newClientRevenue)} Kč`}
                    />
                  )}
                  {data.trialRevenue > 0 && (
                    <RadekPrehledu
                      popisek="Zkušební tréninky"
                      napoveda={`Zaplacené faktury za zkušební trénink v tomto měsíci. Poznají se podle položky faktury se slovem zkušebn. Z toho DPH ${fmt(data.trialRevenueVat)} Kč.`}
                      hodnota={`${fmt(data.trialRevenue)} Kč`}
                      doplnek={`${data.trialCount} trialů`}
                    />
                  )}
                </RadkyPrehledu>
              </>
            )}

            {(data.trialLossCount || 0) > 0 && (
              <>
                <SkupinaPrehledu ikona={ArrowDownLeft} nazev="Co ušlo" />
                <RadkyPrehledu>
                  <RadekPrehledu
                    popisek="Ztracený obrat"
                    napoveda="Odhad obratu, který v tomto měsíci unikl. Za každou prohru se počítá průměrný měsíční přínos 6 700 Kč. Orientační číslo, ne účetní."
                    hodnota={`−${fmt(data.lostRevenue)} Kč`}
                    doplnek={`${data.trialLossCount} proher`}
                    tonHodnoty="text-rose-700"
                  />
                </RadkyPrehledu>
              </>
            )}
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Target}
              nazev="Marketing"
              podtitulek="útrata a návratnost"
              odznak={`${fmt(data.mktSpend)} Kč`}
              napoveda="Výdaje na Meta Ads za vybraný měsíc, součet spendu mezi prvním a posledním dnem měsíce."
            />
            {(avgDeal > 0 || data.roiPct > 0 || data.roi3mPct > 0 || data.roi3mHotovo) && (
              <>
                <SkupinaPrehledu ikona={TrendingUp} nazev="Co to přineslo" />
                <RadkyPrehledu>
                  {avgDeal > 0 && (
                    <RadekPrehledu
                      popisek="Ø deal s DPH"
                      napoveda="Průměrná hodnota jednoho dealu z trialů tohoto měsíce včetně DPH. Obrat z FAPI faktur klientů, jejichž realizovaný trial proběhl v tomto měsíci a v Leady jsou Deal nebo Uzavřeno, dělený počtem těchto dealů."
                      hodnota={`${fmt(avgDeal)} Kč`}
                      doplnek={`${data.trialDealCount} dealů`}
                      tonHodnoty="text-emerald-700"
                    />
                  )}
                  {data.roiPct > 0 && (
                    <RadekPrehledu
                      popisek="ROI"
                      napoveda="Návratnost marketingu za měsíc, atribuce podle data leadu. Revenue z vystavených ne-trial faktur klientů s leadem v tomto měsíci a stagem Deal nebo Uzavřeno plus zaplacené trial faktury měsíce, bez DPH, dělené MKT spendem. Stornované se nepočítají. Zelená od 300 %, žlutá od 150 %."
                      hodnota={`${data.roiPct} %`}
                      doplnek={data.roiRevenue > 0 ? `${fmt(data.roiRevenue)} Kč bez DPH` : undefined}
                      tonHodnoty={tonRoi(data.roiPct)}
                    />
                  )}
                  {data.mktSpend > 0 && (data.roi3mPct > 0 || data.roi3mHotovo) && (
                    <RadekPrehledu
                      popisek="ROI po 3 měsících"
                      napoveda={`Návratnost reklamy s odstupem. Bere všechny faktury klientek, jejichž lead vznikl v tomto měsíci a mají stage Deal nebo Uzavřeno, vystavené do tří měsíců od začátku měsíce, plus zaplacené trialy. Bez DPH, dělené MKT spendem měsíce. Na rozdíl od ROI nad tím nepočítá jen první fakturu, takže se dají měsíce porovnávat mezi sebou.${data.roi3mHotovo ? ` Zatím ${fmt(data.roi3mRevenueNoVat)} Kč bez DPH.` : ""}`}
                      hodnota={`${data.roi3mPct} %`}
                      doplnek={data.roi3mHotovo
                        ? `${data.roi3mFaktur} faktur`
                        : `průběžně do ${data.roi3mDo ? data.roi3mDo.split("-").reverse().map((x) => String(Number(x))).join(". ") : ""}`}
                      tonHodnoty={data.roi3mHotovo ? tonRoi(data.roi3mPct) : "text-slate-600"}
                    />
                  )}
                </RadkyPrehledu>
              </>
            )}

            {data.trialDealCount > 0 && (data.trialDealUnmatchedNames || []).length > 0 && (
              <div className="p-3 sm:p-4">
                <RozbalovaciJmena
                  nazev={`Dealy bez faktury v tomto měsíci (z ${data.trialDealCount})`}
                  pocet={(data.trialDealUnmatchedNames || []).length}
                  tonPoctu="bg-amber-50 text-amber-700"
                  jmena={data.trialDealUnmatchedNames}
                  poznamka="Ověř v kartě klienta a ve FAPI. Klient buď ještě nemá vystavenou fakturu za balíček, nebo má na faktuře jméno zapsané jinak."
                />
              </div>
            )}

            {data.mktSpend > 0 && (
              <>
                <SkupinaPrehledu ikona={Coins} nazev="Co to stálo" />
                <RadkyPrehledu>
                  {data.leads > 0 && (
                    <RadekPrehledu
                      popisek="CPL, cena za lead"
                      napoveda="MKT spend dělený počtem nových leadů v tomto měsíci."
                      hodnota={`${fmt(data.cpl)} Kč`}
                      doplnek={`${data.leads} leadů`}
                    />
                  )}
                  {data.trialCount > 0 && (
                    <RadekPrehledu
                      popisek="Cena za trial"
                      napoveda="Kolik stojí získat klienta, který zaplatil zkušební trénink. MKT spend dělený počtem zaplacených zkušebních faktur v tomto měsíci."
                      hodnota={`${fmt(costPerTrial)} Kč`}
                      doplnek={`${data.trialCount} trialů`}
                    />
                  )}
                  {data.trialDealCount > 0 && (
                    <RadekPrehledu
                      popisek="Cena za nového klienta"
                      napoveda="MKT spend dělený počtem dealů z trialů, tedy trial v tomto měsíci a následně Deal nebo Uzavřeno v Leady. Atribuce přesně odpovídá MKT spendu tohoto měsíce."
                      hodnota={`${fmt(costPerNewClient)} Kč`}
                      doplnek={`${data.trialDealCount} dealů`}
                    />
                  )}
                </RadkyPrehledu>
              </>
            )}
          </KartaPrehledu>

          {(data.monthlyGoal > 0 || data.newClientGoal > 0) && (
            <KartaPrehledu>
              <HlavickaDlazdice
                ikona={Target}
                nazev="Plnění měsíčních cílů"
                napoveda="Pokrok proti cílům za daný měsíc. Prahy barev: do 50 % sytější červená, 50 až 70 % světlejší červená, 70 až 90 % oranžová, od 90 % zelená."
              />
              <div className={`grid grid-cols-1 gap-3 p-3 sm:p-4 ${data.monthlyGoal > 0 && data.newClientGoal > 0 ? "sm:grid-cols-2" : ""}`}>
                {data.monthlyGoal > 0 && (
                  <DlazdiceCile
                    title="Revenue, vystavené faktury"
                    icon={Coins}
                    actual={data.totalInvoicesRevenue || 0}
                    goal={data.monthlyGoal}
                  />
                )}
                {data.newClientGoal > 0 && (
                  <DlazdiceCile
                    title="Noví klienti, obrat"
                    icon={UserPlus}
                    actual={data.newClientRevenue || 0}
                    goal={data.newClientGoal}
                  />
                )}
              </div>
            </KartaPrehledu>
          )}

          {data.missingFromSheetNames?.length > 0 && (
            <Hlaska
              ton="pozor"
              title={`${data.missingFromSheetNames.length} ${data.missingFromSheetNames.length === 1 ? "klient" : "klientů"} s tréninkem v rezervacích se nepodařilo spárovat s kartou klienta`}
            >
              <span className="block">
                Možné důvody: klient nemá kartu v adminu, nebo má jméno zapsané jinak (víc nebo míň slov, velký překlep).
              </span>
              <span className="mt-1 block demo-sensitive">{data.missingFromSheetNames.join(", ")}</span>
            </Hlaska>
          )}

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Filter}
              nazev="Funnel měsíce"
              podtitulek="od leadu k podpisu"
              odznak={`${fmt(leads)} leadů`}
              napoveda="Trialy jsou leady vzniklé v tomto měsíci, které prošly stagem Trial a dál. Deal, Prohra a Čekají jsou jejich dnešní stage, takže jejich součet sedí na počet trialů."
            />
            <RadkyPrehledu>
              <RadekPrehledu popisek="Leady" hodnota={fmt(leads)} />
              {nqLeads > 0 && (
                <RadekPrehledu
                  popisek="Nekvalifikované leady"
                  napoveda="Leady, které neprošly kvalifikací, trial se z nich nedomlouvá."
                  hodnota={fmt(nqLeads)}
                  doplnek={`${leadToNqPct} % z leadů`}
                  tonHodnoty="text-rose-700"
                />
              )}
              <RadekPrehledu
                popisek="Realizované trialy"
                hodnota={fmt(realized)}
                doplnek={`${leadToTrialPct} % z leadů`}
              />
            </RadkyPrehledu>

            <SkupinaPrehledu
              nazev="Jak trialy dopadly"
              napoveda="Lead-atribuce: počítají se leady vzniklé v tomto měsíci a jejich dnešní stage, ne dealy uzavřené tento měsíc ze starších leadů."
            />
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Deal"
                napoveda="Leady vytvořené tento měsíc, které už mají v Leady stage Deal nebo Uzavřeno."
                hodnota={fmt(deal)}
                doplnek={`${trialToDealPct} % z trialů`}
                tonHodnoty="text-emerald-700"
              />
              <RadekPrehledu
                popisek="Prohra"
                napoveda="Leady vytvořené tento měsíc, které už mají v Leady stage Prohra."
                hodnota={fmt(loss)}
                tonHodnoty="text-rose-700"
              />
              <RadekPrehledu
                popisek="Čekají na rozhodnutí"
                napoveda="Leady vytvořené tento měsíc, které prošly trialem, ale ještě se o nich nerozhodlo. Deal plus Prohra plus Čekají se rovná počtu realizovaných trialů."
                hodnota={fmt(undecided)}
              />
              {(deal > 0 || loss > 0) && (
                <RadekPrehledu
                  popisek="Úspěšnost rozhodnutých"
                  napoveda="Deal dělený součtem Deal a Prohra. Čekající se do toho nepočítají."
                  hodnota={`${successPct} %`}
                  tonHodnoty={successPct >= 70 ? "text-emerald-700" : successPct >= 50 ? "text-amber-700" : "text-rose-700"}
                />
              )}
            </RadkyPrehledu>
          </KartaPrehledu>
        </>
      )}
    </Stranka>
  );
}
